import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Copy, RefreshCw, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AICaptionGenerator = ({ barName, barType = "bar", onSelectCaption }) => {
  const [captions, setCaptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCaption, setSelectedCaption] = useState('');
  const [aiConfigured, setAiConfigured] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if AI is configured
  useEffect(() => {
    const checkAIStatus = async () => {
      try {
        const response = await axios.get(`${API}/ai/status`);
        setAiConfigured(response.data.configured);
      } catch (error) {
        console.log('AI status check failed');
      }
    };
    checkAIStatus();
  }, []);

  const generateCaptions = async () => {
    if (!barName) return;
    
    setLoading(true);
    setCaptions([]);
    
    try {
      const response = await axios.post(
        `${API}/ai/suggest-captions?bar_name=${encodeURIComponent(barName)}`
      );
      
      if (response.data.success) {
        setCaptions(response.data.captions);
        toast.success('AI captions generated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate captions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCaption = (caption) => {
    setSelectedCaption(caption);
    if (onSelectCaption) {
      onSelectCaption(caption);
    }
  };

  const copyToClipboard = async (text) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Caption copied!');
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.log('Clipboard API failed, trying fallback');
      }
    }
    
    // Fallback: Create temporary textarea
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      textArea.remove();
      setCopied(true);
      toast.success('Caption copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      textArea.remove();
      toast.info('Select and copy this caption manually', {
        description: text.substring(0, 80) + '...',
        duration: 5000
      });
    }
  };

  if (!aiConfigured) {
    return null;
  }

  return (
    <Card className="glass p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <h4 className="text-white font-semibold">AI Caption Generator</h4>
          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
            Powered by xAI Grok
          </span>
        </div>
        <Button
          size="sm"
          onClick={generateCaptions}
          disabled={loading || !barName}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 animate-spin mr-1" />
          ) : (
            <Sparkles className="w-4 h-4 mr-1" />
          )}
          {loading ? 'Generating...' : 'Generate Captions'}
        </Button>
      </div>

      {captions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400 mb-2">Click to select, then copy to share:</p>
          {captions.map((caption, index) => (
            <div
              key={index}
              onClick={() => handleSelectCaption(caption)}
              className={`p-3 rounded-lg cursor-pointer transition-all ${
                selectedCaption === caption
                  ? 'bg-purple-500/20 border border-purple-500'
                  : 'bg-slate-800/50 border border-slate-700 hover:border-slate-600'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-slate-200 flex-1">{caption}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(caption);
                  }}
                  className="text-slate-400 hover:text-white shrink-0"
                >
                  {copied && selectedCaption === caption ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCaption && (
        <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <p className="text-xs text-green-400 mb-1">Selected Caption:</p>
          <p className="text-sm text-white">{selectedCaption}</p>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-3">
        +5 points for using AI captions • Perfect for Instagram & Twitter
      </p>
    </Card>
  );
};

export default AICaptionGenerator;
